import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TrendingUp } from 'lucide-react';
import { enrichStatusOptions } from '../../shared/utils/crmPageUtils';
import { getCrmStageLabel } from '../../shared/utils/crmConstants';
import { getCrmPipelineStages } from './crmConfigDefaults';
import { useCrmConfig } from './useCrmConfig';
import { resolveClientPartySelection } from '../../shared/utils/crmCrossModuleUtils';
import { useCrmClientPartyPicker } from './crmPickerOptions';
import CrmCoreListPage from './components/CrmCoreListPage';

const OpportunitiesPage = () => {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.crm.pages.opportunitiesPage.${key}`, opts);
  const crmConfig = useCrmConfig();
  const { partyOptions, contacts, clients } = useCrmClientPartyPicker();

  const stageOptions = useMemo(() => enrichStatusOptions(
    getCrmPipelineStages(crmConfig).map((s) => ({
      value: s.value,
      label: getCrmStageLabel(s.value, t),
    })),
  ), [crmConfig, t]);

  return (
    <CrmCoreListPage
      pageKey="opportunitiesPage"
      apiPath="/crm/opportunities"
      icon={TrendingUp}
      showValueTier
      entityRef={{ field: 'opportunity_ref', prefixKey: 'opportunity', fallback: 'OPP' }}
      stageOptions={stageOptions}
      mapRecord={(r) => ({
        ...r,
        display_title: r.title || r.opportunity_name,
        contact_label: r.contact_name || r.contact_company || '—',
      })}
      buildPayload={(form) => {
        const party = resolveClientPartySelection(form.contact_id, { contacts, clients });
        return {
          title: form.title,
          contact_id: party.contactId ? parseInt(party.contactId, 10) : null,
          value: parseFloat(form.value) || 0,
          stage: form.stage || 'lead',
          probability: parseInt(form.probability, 10) || 0,
          expected_close_date: form.expected_close_date || null,
          description: form.description || '',
        };
      }}
      fields={[
        { name: 'title', labelKey: 'field_title', required: true, placeholderKey: 'placeholder_title' },
        { name: 'contact_id', labelKey: 'col_contact', type: 'select', options: partyOptions },
        { name: 'value', labelKey: 'field_value', inputType: 'number', required: true, placeholderKey: 'placeholder_value' },
        { name: 'stage', labelKey: 'field_stage', type: 'select', options: stageOptions, defaultValue: 'lead' },
        { name: 'probability', labelKey: 'field_probability', inputType: 'number', placeholderKey: 'placeholder_probability' },
        { name: 'expected_close_date', labelKey: 'field_close_date', inputType: 'date' },
        { name: 'description', labelKey: 'field_description', type: 'textarea', fullWidth: true },
      ]}
      columns={[
        { key: 'opportunity_ref', labelKey: 'col_ref' },
        { key: 'display_title', labelKey: 'col_title' },
        { key: 'contact_label', labelKey: 'col_contact' },
        { key: 'value', labelKey: 'col_value', type: 'money' },
        { key: 'tier', labelKey: 'col_tier', type: 'tier', derive: (r) => r.value },
        { key: 'probability', labelKey: 'col_probability' },
        { key: 'stage', labelKey: 'col_stage', type: 'status' },
        { key: 'expected_close_date', labelKey: 'col_close_date', type: 'date' },
      ]}
    />
  );
};

export default OpportunitiesPage;
